import clsx from "clsx";
import { Field, Form, Formik } from "formik";
import { TextField } from "formik-material-ui";
import { navigate } from "gatsby-link";
import { Button } from "gatsby-theme-material-ui";
import PropTypes from "prop-types";
import React, { ReactElement, useRef, useState } from "react";
import { Link } from "gatsby";
import {
  Container,
  FormControlLabel,
  LinearProgress,
  Checkbox,
  Theme,
  Typography,
} from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";

// import { isWidthUp } from "@material-ui/core/withWidth";
import Layout from "../components/Layout";
import LocalBusiness from "../components/LocalBusiness";
import Map from "../components/Map";
import useSiteMetadata from "../components/SiteMetadata";
import { graphql } from "gatsby";
import Helmet from "react-helmet";


function encode(data): string {
  return Object.keys(data)
    .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "grid",
      gridArea: "content",
      [theme.breakpoints.down("sm")]: {
        gap: `${theme.spacing(4)}px`
      },
      [theme.breakpoints.up("sm")]: {
        // alignContent: "stretch",
        alignItems: "stretch",
        gap: `${theme.spacing(8)}px`,
        gridAutoFlow: "column",
        gridTemplateColumns: "4fr 3fr",
        paddingBottom: theme.spacing(4),
        paddingTop: theme.spacing(4)
      }
    },
    label: {
      '& a ' : {
        textDecoration: "underline",
        fontWeight: 500,
      }
    },
    title: {
      fontSize: "16pt",
      fontWeight: "bold"
    },
    heading: {
      fontSize: "16pt",
      fontWeight: "bold"
    },
    subheading: {
      fontSize: "14pt",
      fontWeight: "bold"
    },
    description: {},
    segment: {
      
    },
    form: {
      display: "grid",
      gap: `${theme.spacing(2)}px`,
      paddingTop: theme.spacing(4),
      '& .MuiFormControlLabel-labelPlacementStart' : {
        flexDirection: "row",
        marginLeft: 0,
      }
    },
    localBusiness: {
      alignContent: "center",
      backgroundColor: theme.palette.primary.dark,
      color: theme.palette.secondary.contrastText,
      display: "grid",
      marginTop: theme.spacing(8),
      padding: theme.spacing(8)
    },
    map: {
      
    }
  })
);

const Index = ({ 
  formTitle
}): ReactElement => {
  const classes = useStyles();
  const formRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { map } = useSiteMetadata();
  const [checked, setChecked] = useState(false);
  const handleCheckChange = (event) => {
    setChecked(!checked)
  };
  const label = (
    <p className={classes.label}>
    I have read and agree with Williams Lynch <Link to="/privacy-policy">GPDR Policy*</Link>
    </p>
  )
  return (
    <Container className={classes.root} component="main" fluid="fluid">
      <section className={clsx(classes.segment, classes.form)}>
        <Typography component="p">{formTitle}</Typography>
        <Formik
          initialValues={{
            email: "",
            password: ""
          }}
          validate={(values): Partial => {
            const errors: Partial<Values> = {};
            if (!values.email) {
              errors.email = "Required";
            } else if (
              !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)
            ) {
              errors.email = "Invalid email address";
            }
            return errors;
          }}
          onSubmit={(values): void => {
            const form = formRef.current;

            setIsSubmitting(true);

            fetch(form.getAttribute("action"), {
              method: "POST",
              headers: {
                "Content-Type": "application/x-www-form-urlencoded"
              },
              body: encode({
                "form-name": form.getAttribute("name"),
                'subject': "ONLINE ENQUIRY – Contact Form",
                ...values
              })
            })
              .then(() => setIsSubmitting(false))
              .then(() => navigate(form.getAttribute("action")))
              .catch(error => alert(error));
          }}
        >
          {({ submitForm, isSubmitting }): ReactElement => (
            <Form
              action="/contact/thanks/"
              className={classes.form}
              data-netlify-honeypot="bot-field"
              data-netlify="true"
              method="post"
              name="contact"
              ref={formRef}
            >
              {/* The `form-name` hidden field is required to support form submissions without JavaScript */}
              <input type="hidden" name="form-name" value="contact" />
              <input type="hidden" id="subject" name="subject" value="ONLINE ENQUIRY – Contact Form" />

              <Field
                component={TextField}
                label="Name"
                name="name"
                type="name"
                required
              />

              <Field
                component={TextField}
                label="Email"
                name="email"
                type="email"
                required
              />

              <Field
                component={TextField}
                label="Telephone"
                name="telephone"
                type="tel"
                pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                required
              />
              <FormControlLabel
                  value="GPDR"
                  control={
                    <Checkbox
                      name="GPDR"
                      checked={checked}
                      onChange={handleCheckChange}
                      color="secondary"
                    />
                  }
                  labelPlacement="end"
                  label={label}
              />
              {isSubmitting && <LinearProgress />}

              <Button
                variant="contained"
                color="primary"
                disabled={isSubmitting || checked !== true}
                onClick={submitForm}
              >
                Send
                </Button>
            </Form>
          )}
        </Formik>

        <div className={classes.localBusiness}>
          <LocalBusiness
            headingComponent="h1"
            headingVariant="h5"
            addressTitle="Visit Us"
            emailTitle="Email Us"
            telephoneTitle="Call Us"
          />
        </div>
      </section>
      <section className={clsx(classes.segment, classes.map)}>
        <Map
          width="100%"
          // height={isWidthUp("sm") ? "80vh" : "40vh"}
          minHeight="00px"
          center={[-0.08203, 51.50162]}
          markers={[
            {
              geometry: {
                coordinates: [-0.08203, 51.50162]
              },
              properties: {
                title: "Williams Lynch",
                summary: "Lorem ipsum",
                image: null
              }
            }
          ]}
          zoom={18}
          onClick={(): void => {
            window.open(map.url);
          }}
        />
      </section>
    </Container>
  );
};

Index.propTypes = {
  title: PropTypes.string,
  heading: PropTypes.string,
  featuredimage: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  imagewidth: PropTypes.string,
  subheading: PropTypes.string,
  description: PropTypes.string,
  location: PropTypes.object,
  formTitle: PropTypes.string.isRequired,
  helmet: PropTypes.object
};

const ContactPage = ({ data, location }): ReactElement => {
  const { markdownRemark: post } = data;
  const rootUrl = "https://williamslynch.co.uk";

  return (
    <Layout pathname={location.pathname}>
      <Index 
        title={post.frontmatter.title}
        heading={post.frontmatter.heading}
        subheading={post.frontmatter.subheading}
        description={post.frontmatter.description}
        featuredimage={post.frontmatter.featuredimage}
        imagewidth={post.frontmatter.imagewidth}
        formTitle={post.frontmatter.formTitle} 
        helmet={
          <Helmet titleTemplate="%s">
            <title>{`${post.frontmatter.title}`}</title>
            <meta name="description" content={`${post.frontmatter.description}`} />
            <meta name="image" content={`${rootUrl}${post.frontmatter.featuredimage?.childImageSharp.fluid.src}`} />
            <meta name="og:image" content={`${rootUrl}${post.frontmatter.featuredimage?.childImageSharp.fluid.src}`} />
            <meta name="twitter:image" content={`${rootUrl}${post.frontmatter.featuredimage?.childImageSharp.fluid.src}`} />
          </Helmet>
        }
      />
    </Layout>
  );
};

ContactPage.propTypes = {
  data: PropTypes.object.isRequired,
  location: PropTypes.object
};

export default ContactPage;

export const pageQuery = graphql`
  query ContactPage($id: String!) {
    markdownRemark(id: { eq: $id }) {
      html
      frontmatter {
        heading
        subheading
        featuredimage {
          childImageSharp {
            fluid(maxWidth: 960, quality: 64) {
              ...GatsbyImageSharpFluid
            }
          }
        }
        imagewidth
        description
        formTitle
      }
    }
  }
`;
